import React, { useEffect, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { EyeIcon, EyeOffIcon, LockIcon } from 'lucide-react';
import type { AppState, Notificaciones, Perfil, Preferencias } from '../types';
import { Field, PageShell, Toggle, glassLight, inputStyle, T, primaryButton } from '../ui';
function PanelLabel({
  children
}: {
  children: React.ReactNode;
}) {
  return <div style={{
    fontSize: 11,
    fontWeight: 700,
    color: T.textMuted,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8
  }} data-id="element-308" data-mp="2tqsi8h8qk8g9">
      
      <span style={{
      width: 5,
      height: 5,
      borderRadius: '50%',
      background: T.accent,
      boxShadow: `0 0 8px ${T.accent}`
    }} data-id="element-309" data-mp="33q1a94k1jczz" />
      
      {children}
    </div>;
}
const panelVariant = {
  hidden: {
    opacity: 0,
    y: 14
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.34,
      ease: [0.2, 0.8, 0.2, 1]
    }
  }
};
type Tab = 'general' | 'usuarios' | 'roles';
export function Configuracion({
  state,
  setPerfil,
  setNotif,
  setPrefs,
  usuariosNode,
  rolesNode,
  cambiarPassword
}: {
  state: AppState;
  setPerfil: (value: Perfil) => void;
  setNotif: (value: Notificaciones) => void;
  setPrefs: (value: Preferencias) => void;
  cambiarPassword?: (actual: string, nueva: string) => Promise<{
    ok: boolean;
    error?: string;
  }>;
  usuariosNode?: React.ReactNode;
  rolesNode?: React.ReactNode;
}) {
  // ── Draft state local (no guarda hasta presionar "Guardar") ──────────────
  const [draftPerfil, setDraftPerfil] = useState<Perfil>({
    ...state.perfil
  });
  const [draftNotif, setDraftNotif] = useState<Notificaciones>({
    ...state.notif
  });
  const [draftPrefs, setDraftPrefs] = useState<Preferencias>({
    ...state.prefs
  });
  // Cuando cambia el usuario logueado (session), sincronizamos el draft
  useEffect(() => {
    setDraftPerfil({
      ...state.perfil
    });
    setDraftNotif({
      ...state.notif
    });
    setDraftPrefs({
      ...state.prefs
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.perfil.correo]);
  // ── Cambiar contraseña (independiente del "Guardar cambios" de arriba) ────
  const [passActual, setPassActual] = useState('');
  const [passNueva, setPassNueva] = useState('');
  const [passConfirmar, setPassConfirmar] = useState('');
  const [showPassActual, setShowPassActual] = useState(false);
  const [showPassNueva, setShowPassNueva] = useState(false);
  const [passError, setPassError] = useState('');
  const [passOk, setPassOk] = useState(false);
  const [cambiandoPass, setCambiandoPass] = useState(false);
  const handleCambiarPassword = async () => {
    if (!cambiarPassword || cambiandoPass) return;
    setPassError('');
    setPassOk(false);
    if (passNueva !== passConfirmar) {
      setPassError('La confirmación no coincide con la nueva contraseña.');
      return;
    }
    setCambiandoPass(true);
    const {
      ok,
      error
    } = await cambiarPassword(passActual, passNueva);
    setCambiandoPass(false);
    if (!ok) {
      setPassError(error ?? 'No se pudo cambiar la contraseña.');
      return;
    }
    setPassActual('');
    setPassNueva('');
    setPassConfirmar('');
    setPassOk(true);
    setTimeout(() => setPassOk(false), 2500);
  };
  // ── Feedback de guardado ─────────────────────────────────────────────────
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  // Detectar si hay cambios pendientes
  const hasChanges = JSON.stringify(draftPerfil) !== JSON.stringify(state.perfil) || JSON.stringify(draftNotif) !== JSON.stringify(state.notif) || JSON.stringify(draftPrefs) !== JSON.stringify(state.prefs);
  const handleSave = () => {
    setSaving(true);
    setPerfil(draftPerfil);
    setNotif(draftNotif);
    setPrefs(draftPrefs);
    setTimeout(() => {
      setSaving(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }, 350);
  };
  // ── Tabs ──────────────────────────────────────────────────────────────────
  const tabs: {
    id: Tab;
    label: string;
    show: boolean;
  }[] = [{
    id: 'general',
    label: 'General',
    show: true
  }, {
    id: 'usuarios',
    label: 'Usuarios',
    show: !!usuariosNode
  }, {
    id: 'roles',
    label: 'Roles y permisos',
    show: !!rolesNode
  }];
  const visibles = tabs.filter(t => t.show);
  const [tab, setTab] = useState<Tab>('general');
  // Cada vez que se entra/vuelve a la pestaña "General" se limpia el
  // formulario de contraseña: nunca debe quedar texto ni mostrarse en claro
  // de una visita anterior.
  useEffect(() => {
    if (tab !== 'general') return;
    setPassActual('');
    setPassNueva('');
    setPassConfirmar('');
    setShowPassActual(false);
    setShowPassNueva(false);
    setPassError('');
    setPassOk(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  return <PageShell title="Configuración" sub="Perfil, notificaciones, usuarios, roles y conexión del sistema" data-id="element-310" data-mp="288mvxoc3pu9c">
      
      {/* ── Sub-navegación ─────────────────────────────────────────────── */}
      {visibles.length > 1 && <div style={{
      display: 'flex',
      gap: 6,
      padding: 5,
      borderRadius: 14,
      background: T.surfaceSubtle,
      border: `1px solid ${T.border}`,
      width: 'fit-content',
      marginBottom: 20,
      flexWrap: 'wrap'
    }} data-id="element-311" data-mp="3srqce5m2dps">
        
          {visibles.map(t => {
        const active = tab === t.id;
        return <button key={t.id} onClick={() => setTab(t.id)} style={{
          position: 'relative',
          padding: '9px 18px',
          borderRadius: 10,
          border: 'none',
          background: active ? T.accentSoft : 'transparent',
          color: active ? T.accent : T.textMuted,
          fontSize: 13,
          fontWeight: active ? 700 : 600,
          cursor: 'pointer'
        }} data-id="element-312" data-mp="iohfuma098z9">
              
                {t.label}
              </button>;
      })}
        </div>}

      {/* ── Tab General ──────────────────────────────────────────────────── */}
      {tab === 'general' && <>
          <motion.div initial="hidden" animate="show" variants={{
        hidden: {},
        show: {
          transition: {
            staggerChildren: 0.06
          }
        }
      }} style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit,minmax(290px,1fr))',
        gap: 16
      }} data-id="element-313" data-mp="2slwrc5y62v90">
          
            {/* ── Perfil ───────────────────────────────────────────────── */}
            <motion.div variants={panelVariant} style={{
          ...glassLight,
          padding: 20
        }} data-id="element-314" data-mp="3c81hy4paaebe">
            
              <PanelLabel data-id="element-315" data-mp="3l8zj8f8c87qa">Perfil</PanelLabel>

              <Field label="Nombre" data-id="element-316" data-mp="3laansh9lxjig">
                <input className="getr-input" style={inputStyle} value={draftPerfil.nombre} onChange={e => setDraftPerfil({
              ...draftPerfil,
              nombre: e.target.value
            })} data-id="element-317" data-mp="3l76fb7p401lz" />
              
              </Field>
              <Field label="Correo" data-id="element-318" data-mp="6dywfd6o9u40">
                <input className="getr-input" style={inputStyle} value={draftPerfil.correo} onChange={e => setDraftPerfil({
              ...draftPerfil,
              correo: e.target.value
            })} data-id="element-319" data-mp="6f0lwmofqj59" />
              
              </Field>
              <Field label="Rol" data-id="element-320" data-mp="1lpeajbstyca">
                <input className="getr-input" style={{
              ...inputStyle,
              color: T.textMuted
            }} value={draftPerfil.rol} readOnly data-id="element-321" data-mp="1kmn0aakdtvu" />
              
              </Field>
            </motion.div>

            {/* ── Seguridad / Cambiar contraseña ──────────────────────── */}
            {cambiarPassword && <motion.div variants={panelVariant} style={{
          ...glassLight,
          padding: 20
        }} data-id="element-322" data-mp="39w0m71c3hpig">
            
              <PanelLabel data-id="element-323" data-mp="181rn8z1ehd38">Seguridad</PanelLabel>

              <Field label="Contraseña actual" data-id="element-324" data-mp="1832niovn02pl">
                <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }} data-id="element-325" data-mp="1d1w2klqko1rv">
                  <input className="getr-input" type={showPassActual ? 'text' : 'password'} style={{
                ...inputStyle,
                paddingRight: 38
              }} value={passActual} onChange={e => setPassActual(e.target.value)} autoComplete="off" name="perfil-pass-actual" placeholder="••••••••" data-id="element-326" data-mp="r7op1612tgm" />
                  <button type="button" onClick={() => setShowPassActual(s => !s)} aria-label={showPassActual ? 'Ocultar contraseña' : 'Mostrar contraseña'} style={{
                position: 'absolute',
                right: 8,
                background: 'transparent',
                border: 'none',
                color: T.textMuted,
                display: 'flex',
                cursor: 'pointer',
                padding: 4
              }} data-id="element-327" data-mp="3i6o8k7cnfktd">
                    
                    {showPassActual ? <EyeOffIcon size={16} data-id="element-328" data-mp="12f0f4lwugafb" /> : <EyeIcon size={16} data-id="element-329" data-mp="xiji127o2lq3" />}
                  </button>
                </div>
              </Field>

              <Field label="Nueva contraseña" data-id="element-330" data-mp="152klg1u4ga3b">
                <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }} data-id="element-331" data-mp="151izv3fhc57c">
                  <input className="getr-input" type={showPassNueva ? 'text' : 'password'} style={{
                ...inputStyle,
                paddingRight: 38
              }} value={passNueva} onChange={e => setPassNueva(e.target.value)} autoComplete="new-password" name="perfil-pass-nueva" placeholder="Mínimo 6 caracteres" data-id="element-332" data-mp="1er2kadiuc74b" />
                  <button type="button" onClick={() => setShowPassNueva(s => !s)} aria-label={showPassNueva ? 'Ocultar contraseña' : 'Mostrar contraseña'} style={{
                position: 'absolute',
                right: 8,
                background: 'transparent',
                border: 'none',
                color: T.textMuted,
                display: 'flex',
                cursor: 'pointer',
                padding: 4
              }} data-id="element-333" data-mp="r2jj0hy7kulq">
                    
                    {showPassNueva ? <EyeOffIcon size={16} data-id="element-334" data-mp="2qqac1ow62gp2" /> : <EyeIcon size={16} data-id="element-335" data-mp="2qozbdxy0pp26" />}
                  </button>
                </div>
              </Field>

              <Field label="Confirmar nueva contraseña" data-id="element-336" data-mp="2uqavxp21bk3i">
                <input className="getr-input" type={showPassNueva ? 'text' : 'password'} style={inputStyle} value={passConfirmar} onChange={e => setPassConfirmar(e.target.value)} autoComplete="new-password" name="perfil-pass-confirmar" placeholder="Repite la nueva contraseña" data-id="element-337" data-mp="2zkorbrgp5r86" />
              </Field>

              {passError && <div style={{
            background: T.dangerSoft,
            border: '1px solid rgba(220,38,38,.3)',
            color: '#dc2626',
            borderRadius: 10,
            padding: '8px 12px',
            fontSize: 12.5,
            marginBottom: 10
          }} data-id="element-338" data-mp="2bu2vcyjyvxej">
                  
                  {passError}
                </div>}
              {passOk && <div style={{
            color: T.ok,
            fontSize: 12.5,
            fontWeight: 600,
            marginBottom: 10
          }} data-id="element-339" data-mp="10ghho2dq477">
                  
                  ¡Contraseña actualizada correctamente!
                </div>}

              <button onClick={handleCambiarPassword} disabled={cambiandoPass || !passActual || !passNueva || !passConfirmar} style={{
            ...primaryButton,
            width: '100%',
            justifyContent: 'center',
            opacity: cambiandoPass || !passActual || !passNueva || !passConfirmar ? 0.5 : 1,
            cursor: cambiandoPass || !passActual || !passNueva || !passConfirmar ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }} data-id="element-340" data-mp="1bth9c30uuflu">
                
                <LockIcon size={14} data-id="element-341" data-mp="ny4voasqsb19" />
                {cambiandoPass ? 'Actualizando…' : 'Cambiar contraseña'}
              </button>
            </motion.div>}

            {/* ── Notificaciones ───────────────────────────────────────── */}
            <motion.div variants={panelVariant} style={{
          ...glassLight,
          padding: 20
        }} data-id="element-342" data-mp="gkdnvufuswdl">
            
              <PanelLabel data-id="element-343" data-mp="lcdrpxz1o9fg">Notificaciones</PanelLabel>
              <Toggle label="Alertas fuera de rango" checked={draftNotif.fueraRango} onChange={() => setDraftNotif({
            ...draftNotif,
            fueraRango: !draftNotif.fueraRango
          })} data-id="element-344" data-mp="6mzjit77fy1n" />
            
              <Toggle label="Resumen diario" checked={draftNotif.resumen} onChange={() => setDraftNotif({
            ...draftNotif,
            resumen: !draftNotif.resumen
          })} data-id="element-345" data-mp="zb3qrj5fcj6g" />
            
              <Toggle label="Cambios de auditoría" checked={draftNotif.auditoria} onChange={() => setDraftNotif({
            ...draftNotif,
            auditoria: !draftNotif.auditoria
          })} data-id="element-346" data-mp="qbgnssx1r5ba" />
            
              <Toggle label="Enviar por correo" checked={draftNotif.email} onChange={() => setDraftNotif({
            ...draftNotif,
            email: !draftNotif.email
          })} data-id="element-347" data-mp="3b04r70cto2lq" />
            
            </motion.div>

            {/* ── Preferencias ─────────────────────────────────────────── */}
            <motion.div variants={panelVariant} style={{
          ...glassLight,
          padding: 20
        }} data-id="element-348" data-mp="32j46a3abz326">
            
              <PanelLabel data-id="element-349" data-mp="3hettu5v5zo90">Preferencias</PanelLabel>
              <Field label="Lux mínimo global" data-id="element-350" data-mp="3cj4pzqc43l6w">
                <input className="getr-input" style={inputStyle} type="number" value={draftPrefs.luxMin} onChange={e => setDraftPrefs({
              ...draftPrefs,
              luxMin: Number(e.target.value)
            })} data-id="element-351" data-mp="3cg8t85c032k1" />
              
              </Field>
              <Field label="Idioma" data-id="element-352" data-mp="2yj3nk6g785pb">
                <select className="getr-input" style={inputStyle} value={draftPrefs.idioma} onChange={e => setDraftPrefs({
              ...draftPrefs,
              idioma: e.target.value
            })} data-id="element-353" data-mp="2yk4z0c14mu7k">
                
                  <option data-id="element-354" data-mp="213g5d5mbyy99">Español</option>
                  <option data-id="element-355" data-mp="212eogkimvhjm">English</option>
                </select>
              </Field>
              <Field label="Tema" data-id="element-356" data-mp="14cue206glaub">
                <select className="getr-input" style={inputStyle} value={draftPrefs.tema} onChange={e => setDraftPrefs({
              ...draftPrefs,
              tema: e.target.value
            })} data-id="element-357" data-mp="149q38jyhnguo">
                
                  <option data-id="element-358" data-mp="1i2o76uozyh9u">Oscuro</option>
                  <option data-id="element-359" data-mp="1i3pq1lc98824">Claro</option>
                  <option data-id="element-360" data-mp="1i4sg5r1r66gz">Automático</option>
                </select>
              </Field>
            </motion.div>
          </motion.div>

          {/* ── Botón Guardar ─────────────────────────────────────────── */}
          <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} style={{
        marginTop: 24,
        display: 'flex',
        alignItems: 'center',
        gap: 14
      }} data-id="element-361" data-mp="1w2yz8121m1wl">
          
            <button onClick={handleSave} disabled={saving || !hasChanges} style={{
          ...primaryButton,
          opacity: !hasChanges || saving ? 0.5 : 1,
          cursor: !hasChanges || saving ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          transition: 'opacity 0.2s'
        }} data-id="element-362" data-mp="6niztddx7ujw">
            
              {saving ? <>
                  <span style={{
              width: 14,
              height: 14,
              border: '2px solid rgba(255,255,255,0.3)',
              borderTopColor: '#ffffff',
              borderRadius: '50%',
              display: 'inline-block',
              animation: 'spin 0.7s linear infinite'
            }} data-id="element-363" data-mp="3gl1eusdb1yzr" />
              
                  Guardando…
                </> : <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" data-id="element-364" data-mp="43mo6zxtqnc7">
                
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" data-id="element-365" data-mp="2svymoql1onr0" />
                    <polyline points="17 21 17 13 7 13 7 21" data-id="element-366" data-mp="2osiq1u8z6neb" />
                    <polyline points="7 3 7 8 15 8" data-id="element-367" data-mp="2qb0md5v0evj4" />
                  </svg>
                  Guardar cambios
                </>}
            </button>

            {/* Indicador de cambios pendientes */}
            {hasChanges && !saved && <motion.span initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} style={{
          fontSize: 12,
          color: T.textMuted
        }} data-id="element-368" data-mp="2gdcucnosg24n">
            
                Cambios sin guardar
              </motion.span>}

            {/* Confirmación de guardado */}
            {saved && <motion.span initial={{
          opacity: 0,
          x: -6
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0
        }} style={{
          fontSize: 13,
          fontWeight: 600,
          color: T.ok,
          display: 'flex',
          alignItems: 'center',
          gap: 6
        }} data-id="element-369" data-mp="2cbrfwxwlvfsc">
            
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" data-id="element-370" data-mp="1sn8o08k7to9y">
              
                  <polyline points="20 6 9 17 4 12" data-id="element-371" data-mp="19z888rszc281" />
                </svg>
                ¡Guardado correctamente!
              </motion.span>}
          </motion.div>

          {/* Keyframe para el spinner */}
          <style data-id="element-372" data-mp="1j0o8i6q2ynyj">{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </>}

      {/* ── Tab Usuarios ─────────────────────────────────────────────────── */}
      {tab === 'usuarios' && usuariosNode && <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3
    }} data-id="element-373" data-mp="1svg4x77xf4rq">
        
          {usuariosNode}
        </motion.div>}

      {/* ── Tab Roles ────────────────────────────────────────────────────── */}
      {tab === 'roles' && rolesNode && <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3
    }} data-id="element-374" data-mp="2bnshemsanmfl">
        
          {rolesNode}
        </motion.div>}
    </PageShell>;
}